import React, { MutableRefObject, useCallback } from "react";
import { Button } from "@mui/material";
import { Form, useField } from "react-final-form";
import { Checkboxes } from "mui-rff";

import {
  Modal,
  ModalActions,
  ModalComponent,
  ModalContent,
  ModalTitle,
  Select,
  showToast,
} from "@/features/shared/components";
import {
  UpdateLegacyWarningSettingBody,
  UpdateLegacyWarningSettingBodyLegacyWarningSettings,
} from "@/lib/api";
import {
  useCreateDashboardWarningMutation,
  useUpdateDashboardWarningMutation,
} from "@/features/admin/features/dashboards";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useDashboardWarningsQuery } from "@/features/admin/features/dashboards/hooks/queries";
import { useTranslation } from "@/lib/i18n";

import { warningOptions, warningTypes } from "./consts";
import { LegacyDashboardWarningSettingKey } from "./legacy-warning-setting-keys";
import { toUpdateWarningSettings } from "./warning-settings.utils";

type FormValues = UpdateLegacyWarningSettingBody | undefined;

type Props = {
  initialValues?: FormValues;
  onClose?: () => void;
  isNewWarning?: boolean;
};

type WarningOptionsFormValues = Record<
  LegacyDashboardWarningSettingKey,
  { label: string; value: string }[]
>;

const WarningsSettingsDialog: React.ForwardRefRenderFunction<ModalComponent, Props> = (
  { onClose, initialValues, isNewWarning = false },
  ref,
) => {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();

  const warningQuery = useDashboardWarningsQuery(dashboard.id);
  const createDashboardWarning = useCreateDashboardWarningMutation();
  const updateDashboardWarning = useUpdateDashboardWarningMutation();

  const modalRef = ref as MutableRefObject<ModalComponent>;

  const handleClose = useCallback(() => {
    modalRef.current?.close();
  }, [modalRef]);

  /*
    Ideally you would wnat to create two seperate modals for create and update, but to keep this simple
    we will just use one modal for both. If the logic gets more complex consider splitting this up.
  */
  const handleSubmit = (values: FormValues) => {
    if (!values) return;

    if (values.legacy_warning_settings) {
      if ("type" in values.legacy_warning_settings) {
        delete values.legacy_warning_settings.type;
      }
    }

    const legacyWarningSettings: UpdateLegacyWarningSettingBodyLegacyWarningSettings = {
      ...toUpdateWarningSettings(warningQuery.data),
      ...values.legacy_warning_settings,
    };

    if (isNewWarning) {
      createDashboardWarning.mutate(
        {
          dashboardId: dashboard.id,
          body: {
            legacy_warning_settings: legacyWarningSettings,
          },
        },
        {
          onSuccess: () => {
            handleClose();
            showToast(t("admin.messages.warnings_created"));
          },
        },
      );
    } else {
      updateDashboardWarning.mutate(
        {
          dashboardId: dashboard.id,
          body: {
            legacy_warning_settings: legacyWarningSettings,
          },
        },
        {
          onSuccess: () => {
            handleClose();
            showToast(t("admin.messages.warnings_updated"));
          },
        },
      );
    }
  };

  return (
    <Modal onClose={onClose} ref={modalRef}>
      <Form<FormValues>
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={({ handleSubmit }) => (
          <>
            <ModalTitle>{t("admin.dashboard_settings.warnings_settings.title")}</ModalTitle>

            <ModalContent>
              <WarningsForm
                onSubmit={handleSubmit}
                initialValues={initialValues}
                isNewWarning={isNewWarning}
              />
            </ModalContent>
            <ModalActions>
              <Button key="cancel" onClick={handleClose}>
                {t("general.cancel")}
              </Button>
              <Button key="save" onClick={handleSubmit}>
                {t("general.save")}
              </Button>
            </ModalActions>
          </>
        )}
      />
    </Modal>
  );
};

const WarningsForm = ({
  onSubmit,
  initialValues,
  isNewWarning,
}: {
  onSubmit: () => void;
  initialValues: FormValues;
  isNewWarning: boolean;
}) => {
  const { t } = useTranslation();
  const translationKey = "admin.dashboard_settings.warnings_settings.fields";
  const typeField = useField("legacy_warning_settings.type");

  const transformOptionsToFormValues = (options: string[]) =>
    options.map((option) => {
      const transformName = option.replace(/_/g, " ");
      return {
        label: transformName,
        value: option,
      };
    });

  const warningOptionsFormValues = React.useMemo<WarningOptionsFormValues>(
    () => ({
      warnings_water_authorities: transformOptionsToFormValues(
        warningOptions.warnings_water_authorities,
      ),
      warnings_provinces: transformOptionsToFormValues(warningOptions.warnings_provinces),
    }),
    [],
  );

  const initialType = initialValues ? Object.keys(initialValues)[0] : "";
  // TODO: fix default value for the checkboxes

  return (
    <form onSubmit={onSubmit} noValidate>
      <Select
        required
        name="legacy_warning_settings.type"
        initialValue={initialType}
        label={t(`${translationKey}.type.label`)}
        options={warningTypes}
        sx={{ mb: 2 }}
        disabled={!isNewWarning}
      />
      {(typeField.input.value || initialType) && (
        <Checkboxes
          required
          name={`legacy_warning_settings.${
            (typeField.input.value as keyof WarningOptionsFormValues) || initialType
          }[]`}
          label={t(`${translationKey}.options.label`)}
          data={
            warningOptionsFormValues[
              (typeField.input.value as keyof WarningOptionsFormValues) || initialType
            ]
          }
        />
      )}
    </form>
  );
};

export default React.forwardRef(WarningsSettingsDialog);
